import { Comparator, type Condition } from "@remi/types/query-builder";

import type { OptionData } from "../types/option-data";

type VerboseOptionData<T extends string> = OptionData<T> & {
  parent?: string;
};

const getSearchValue = <T extends string>(option: OptionData<T>): string => {
  return option.hasChildren !== true && option.searchValue ? option.searchValue : option.label;
};

const getRaw = (value: string | undefined): string => {
  return value?.replace(/[^a-zA-Z0-9]/g, "").toLowerCase() ?? "";
};

const filterList = <T extends string>(
  unfilteredData: readonly VerboseOptionData<T>[],
  query: string,
  filteredData: Set<VerboseOptionData<T>> = new Set(),
  parent?: string,
): VerboseOptionData<T>[] => {
  const terms = query.split(/\s+/).map(getRaw).filter(Boolean);
  unfilteredData.forEach((option) => {
    const searchValue = getSearchValue(option);
    const haystack = `${getRaw(searchValue)} ${parent ? getRaw(parent) : ""}`;

    if (searchValue && terms.every((term) => haystack.includes(term))) {
      if (option.hasChildren !== true || option.options.length !== 0) {
        option.parent = parent && terms.length > 0 ? parent : undefined;
        filteredData.add(option);
      }
    }
    if (option.hasChildren === true) {
      filterList(option.options, query, filteredData, option.label);
    }
  });

  return Array.from(filteredData);
};

type LeafOptionData<T extends string> = Extract<OptionData<T>, { value: T }> & { parent?: string };

const flattenOptions = <T extends string>(data: readonly OptionData<T>[], parent?: string): LeafOptionData<T>[] =>
  data.flatMap((option) =>
    option.hasChildren === true ? flattenOptions(option.options, option.label) : [{ ...option, parent }],
  );

const NegatedComparators: readonly Comparator[] = [Comparator.NOT_IN, Comparator.NOT_EQUALS];

interface EffectiveSelection {
  values: string[];
  negated: boolean;
  complemented: boolean;
}

const ArrayComparators: readonly Comparator[] = [Comparator.IN, Comparator.NOT_IN, Comparator.CONTAINS_ALL];

const getDomainValues = (data: readonly OptionData<string>[]): string[] => [
  ...new Set(flattenOptions(data).map((option) => option.value)),
];

const toValueArray = (value: Condition["value"] | undefined): string[] | undefined => {
  if (Array.isArray(value)) {
    return value;
  }
  if (typeof value === "string") {
    return [value];
  }
  return undefined;
};

const getEffectiveSelection = ({
  condition,
  data,
  closedDomain,
}: {
  condition?: Condition;
  data: readonly OptionData<string>[];
  closedDomain: boolean;
}): EffectiveSelection => {
  const raw = toValueArray(condition?.value);

  if (!condition || !NegatedComparators.includes(condition.comparator)) {
    return { values: raw ?? [], negated: false, complemented: false };
  }

  // Complementing needs the whole domain: a partial async list, or a value shape this cannot read,
  // would yield an empty exclusion set and so present the entire domain as selected.
  if (!closedDomain || raw === undefined) {
    return { values: raw ?? [], negated: true, complemented: false };
  }

  const excluded = new Set(raw);
  return {
    values: getDomainValues(data).filter((value) => !excluded.has(value)),
    negated: false,
    complemented: true,
  };
};

const complementSelection = (selected: string[], data: readonly OptionData<string>[]): string[] => {
  const included = new Set(selected);
  return getDomainValues(data).filter((value) => !included.has(value));
};

const getArrayComparator = (existing?: Comparator): Comparator =>
  existing !== undefined && ArrayComparators.includes(existing) ? existing : Comparator.IN;

const findSelectedOption = <T extends string>(
  data: readonly OptionData<T>[],
  selectedValue: T | null,
): OptionData<T> | null => {
  if (!selectedValue) {
    return null;
  }
  for (const item of data) {
    if (item.hasChildren !== true && item.value === selectedValue) {
      return item;
    }
    if (item.hasChildren === true) {
      const childOption = findSelectedOption(item.options, selectedValue);
      if (childOption) {
        return childOption;
      }
    }
  }
  return null;
};

export type { VerboseOptionData };
export {
  complementSelection,
  filterList,
  findSelectedOption,
  flattenOptions,
  getArrayComparator,
  getEffectiveSelection,
  getRaw,
  getSearchValue,
};
